import { motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { useLogoFlutter } from '@/assets/icons/useLogoFlutter';
import { cn } from '@/lib/utils';

import { YStack } from './layout';

interface SplashProps {
  reason?: string;
  context?: Record<string, unknown>;
  animate?: boolean;
}

export const Splash = ({ reason, context, animate = false }: SplashProps) => {
  const mountedAt = useRef(Date.now());
  const { wingAngle } = useLogoFlutter({ continuous: animate });

  useEffect(() => {
    const ts = mountedAt.current;
    console.info('[Splash] mounted', { reason, ...context });
    return () => {
      console.info('[Splash] unmounted', {
        reason,
        visibleMs: Date.now() - ts,
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <YStack className="items-center justify-center min-h-screen gap-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <AppLogo size={50} wingAngle={animate ? wingAngle : undefined} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <span
          className={cn(
            'relative',
            'inline-block',
            'text-xs',
            'font-medium',
            'select-none',
            'bg-[linear-gradient(to_right,var(--foreground-muted)_0%,var(--foreground-muted)_67.5%,var(--foreground-default)_72.5%,var(--foreground-default)_87.5%,var(--foreground-muted)_92.5%,var(--foreground-muted)_100%)]',
            'bg-[length:200%_100%]',
            'bg-clip-text',
            'text-transparent',
            '[background-position:0%_0]',
            'animate-[splashShimmer_1.7s_cubic-bezier(.6,.6,0,1)_forwards_infinite]'
          )}
        >
          Setting up your Littlebird
          <style>{`
            @keyframes splashShimmer {
              0% {
                background-position: 0% 0;
              }
              100% {
                background-position: -170% 0;
              }
            }
          `}</style>
        </span>
      </motion.div>
    </YStack>
  );
};
